/**
 * Summary Cards Component
 * Displays key metrics for enterprise usage
 */

import React from 'react';
import { Activity, Users, TrendingUp, TrendingDown } from 'lucide-react';
import { SummaryStats, FEATURE_LABELS } from '../types';

interface SummaryCardsProps {
  summary: SummaryStats;
}

const BRAND_PRIMARY = '#03e8d3';
const BRAND_SUCCESS = '#10B981';
const BRAND_WARNING = '#F59E0B';
const BRAND_PURPLE = '#8B5CF6';

interface MetricCardProps {
  title: string;
  value: string | number;
  subtitle?: string;
  icon: React.ReactNode;
  color: string;
}

const MetricCard: React.FC<MetricCardProps> = ({ title, value, subtitle, icon, color }) => {
  return (
    <div className="bg-white rounded-xl shadow-sm hover:shadow-md transition-all duration-200 border border-gray-100 p-6">
      <div className="flex items-start justify-between mb-3">
        <div className="p-3 rounded-lg" style={{ backgroundColor: `${color}20` }}>
          <div style={{ color }}>{icon}</div>
        </div>
      </div>
      <p className="text-sm font-medium text-gray-600 mb-1">{title}</p>
      <p className="text-3xl font-mono font-bold text-gray-900 mb-2">{value}</p>
      {subtitle && <p className="text-sm text-gray-500">{subtitle}</p>}
    </div>
  );
};

export const SummaryCards: React.FC<SummaryCardsProps> = ({ summary }) => {
  const formatFeatureName = (name: string | null): string => {
    if (!name) return 'N/A';
    return FEATURE_LABELS[name] || name.replace(/_/g, ' ').replace(/\b\w/g, l => l.toUpperCase());
  };

  return (
    <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
      <MetricCard
        title="Total Runs This Period"
        value={summary.total_runs.toLocaleString()}
        subtitle="Across all features"
        icon={<Activity className="w-6 h-6" />}
        color={BRAND_PRIMARY}
      />
      <MetricCard
        title="Active Enterprise Customers"
        value={summary.active_customers}
        subtitle={`of ${summary.total_enterprise_customers} total`}
        icon={<Users className="w-6 h-6" />}
        color={BRAND_SUCCESS}
      />
      <MetricCard
        title="Most Used Feature"
        value={formatFeatureName(summary.most_used_feature)}
        subtitle={
          summary.most_used_feature && summary.feature_breakdown[summary.most_used_feature]
            ? `${summary.feature_breakdown[summary.most_used_feature].toLocaleString()} runs`
            : undefined
        }
        icon={<TrendingUp className="w-6 h-6" />}
        color={BRAND_WARNING}
      />
      <MetricCard
        title="Least Used Feature"
        value={formatFeatureName(summary.least_used_feature)}
        subtitle={
          summary.least_used_feature && summary.feature_breakdown[summary.least_used_feature]
            ? `${summary.feature_breakdown[summary.least_used_feature].toLocaleString()} runs`
            : undefined
        }
        icon={<TrendingDown className="w-6 h-6" />}
        color={BRAND_PURPLE}
      />
    </div>
  );
};

export default SummaryCards;
